import { useState, useEffect } from 'react';
import { getCompanies, createCompany, updateCompany, deleteCompany, getCompanyMemoryStats, clearCompanyMemories } from '../api/companies';
import { useToast } from '../context/ToastContext';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Topbar } from '../layout/Topbar';
import type { Company } from '../types';

export function CompaniesPage() {
  const { addToast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Company | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmClearMemories, setConfirmClearMemories] = useState(false);
  const [memoryStats, setMemoryStats] = useState<{ count: number } | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDocument, setFormDocument] = useState('');
  const [formStatus, setFormStatus] = useState<Company['status']>('ACTIVE');
  const [formMode, setFormMode] = useState<Company['botAutomationMode']>('HUMANIZED');
  const [formAiName, setFormAiName] = useState('Assistente virtual');
  const [formAiTone, setFormAiTone] = useState('WARM_PROFESSIONAL');
  const [formAiContext, setFormAiContext] = useState('');
  const [formAiInstructions, setFormAiInstructions] = useState('');
  const [formAiMemory, setFormAiMemory] = useState(true);
  const [formAiThreshold, setFormAiThreshold] = useState(0.7);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getCompanies();
      setCompanies(data);
    } catch {
      addToast('error', 'Falha ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const loadMemoryStats = async (id: string) => {
    try {
      const stats = await getCompanyMemoryStats(id);
      setMemoryStats(stats);
    } catch {
      setMemoryStats(null);
    }
  };

  const clearForm = () => {
    setSelected(null);
    setFormName(''); setFormDocument(''); setFormStatus('ACTIVE');
    setFormMode('HUMANIZED'); setFormAiName('Assistente virtual');
    setFormAiTone('WARM_PROFESSIONAL'); setFormAiContext('');
    setFormAiInstructions(''); setFormAiMemory(true); setFormAiThreshold(0.7);
    setMemoryStats(null);
  };

  const selectCompany = (c: Company) => {
    setSelected(c);
    setFormName(c.name); setFormDocument(c.document);
    setFormStatus(c.status); setFormMode(c.botAutomationMode);
    setFormAiName(c.aiAssistantName); setFormAiTone(c.aiTone);
    setFormAiContext(c.aiBusinessContext || '');
    setFormAiInstructions(c.aiInstructions || '');
    setFormAiMemory(c.aiMemoryEnabled); setFormAiThreshold(c.aiConfidenceThreshold);
    loadMemoryStats(c.id);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formDocument.trim()) return;
    setSaving(true);
    try {
      if (selected) {
        const updated = await updateCompany(selected.id, {
          name: formName, document: formDocument, status: formStatus,
          botAutomationMode: formMode, aiAssistantName: formAiName,
          aiTone: formAiTone, aiBusinessContext: formAiContext || null,
          aiInstructions: formAiInstructions || null,
          aiMemoryEnabled: formAiMemory, aiConfidenceThreshold: formAiThreshold,
        });
        setCompanies((prev) => prev.map((c) => c.id === updated.id ? updated : c));
        addToast('success', 'Empresa atualizada');
      } else {
        const created = await createCompany({ name: formName, document: formDocument, status: formStatus });
        setCompanies((prev) => [...prev, created]);
        addToast('success', 'Empresa criada');
      }
      clearForm();
    } catch {
      addToast('error', 'Falha ao salvar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCompany(id);
      setCompanies((prev) => prev.filter((c) => c.id !== id));
      if (selected?.id === id) clearForm();
      addToast('success', 'Empresa excluída');
    } catch {
      addToast('error', 'Falha ao excluir empresa');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleClearMemories = async () => {
    if (!selected) return;
    try {
      await clearCompanyMemories(selected.id);
      setMemoryStats({ count: 0 });
      addToast('success', 'Memórias do agente IA limpas');
    } catch {
      addToast('error', 'Falha ao limpar memórias');
    } finally {
      setConfirmClearMemories(false);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="Empresas" subtitle="Gerenciamento de empresas e agente de IA." />

      <div className="records-layout">
        <div className="panel">
          <div className="panel-header">
            <h2>Empresas ({companies.length})</h2>
            <button onClick={clearForm}>+ Nova</button>
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : companies.length === 0 ? (
            <div className="empty-state"><p>Nenhuma empresa cadastrada.</p></div>
          ) : (
            <div className="data-list" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {companies.map((c) => (
                <div key={c.id} className={`data-list-item${selected?.id === c.id ? ' active' : ''}`}
                  onClick={() => selectCompany(c)}>
                  <div>
                    <div className="item-name">{c.name}</div>
                    <div className="item-detail">{c.document} — {c.botAutomationMode === 'AUTOMATIC' ? 'Automático' : 'Humanizado'}</div>
                  </div>
                  <span className={`pill ${c.status === 'ACTIVE' ? 'success' : c.status === 'SUSPENDED' ? 'warning' : 'neutral'}`}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-header">
            <h2>{selected ? 'Editar' : 'Nova'} empresa</h2>
          </div>
          <div className="panel-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="form-stack">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label>Nome <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Provedor Ltda" /></label>
                <label>Documento <input value={formDocument} onChange={(e) => setFormDocument(e.target.value)} placeholder="CNPJ" /></label>
              </div>
              <label>Modo do bot
                <select value={formMode} onChange={(e) => setFormMode(e.target.value as Company['botAutomationMode'])}>
                  <option value="HUMANIZED">Humanizado — triagem e filas humanas</option>
                  <option value="AUTOMATIC">Automático — IA e autoatendimento SGP</option>
                </select>
              </label>
              <label>Status
                <select value={formStatus} onChange={(e) => setFormStatus(e.target.value as Company['status'])}>
                  <option value="ACTIVE">Ativa</option>
                  <option value="INACTIVE">Inativa</option>
                  <option value="SUSPENDED">Suspensa</option>
                </select>
              </label>

              <fieldset style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
                <legend style={{ fontWeight: 600, fontSize: '0.786rem', color: 'var(--text-secondary)' }}>🤖 Agente de IA</legend>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label>Nome <input value={formAiName} onChange={(e) => setFormAiName(e.target.value)} /></label>
                  <label>Tom
                    <select value={formAiTone} onChange={(e) => setFormAiTone(e.target.value)}>
                      <option value="WARM_PROFESSIONAL">Acolhedor</option>
                      <option value="DIRECT">Direto</option>
                      <option value="INFORMAL">Informal</option>
                      <option value="FORMAL">Formal</option>
                    </select>
                  </label>
                </div>
                <label style={{ marginTop: 8 }}>Contexto do negócio
                  <textarea value={formAiContext} onChange={(e) => setFormAiContext(e.target.value)} rows={2} placeholder="Ex: Provedor de internet fibra óptica em São Paulo..." />
                </label>
                <label>Instruções de atendimento
                  <textarea value={formAiInstructions} onChange={(e) => setFormAiInstructions(e.target.value)} rows={2} placeholder="Ex: Sempre perguntar o endereço antes de prosseguir..." />
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                  <label>Confiança mínima ({formAiThreshold})
                    <input type="range" min={0.5} max={0.95} step={0.05} value={formAiThreshold}
                      onChange={(e) => setFormAiThreshold(parseFloat(e.target.value))} />
                  </label>
                  <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 22 }}>
                    <input type="checkbox" checked={formAiMemory} onChange={(e) => setFormAiMemory(e.target.checked)} />
                    Memorizar clientes
                  </label>
                </div>
              </fieldset>

              {memoryStats !== null && (
                <div className="flex gap-sm" style={{ alignItems: 'center' }}>
                  <span className="text-sm text-secondary">🧠 {memoryStats.count} memórias de clientes</span>
                  {memoryStats.count > 0 && (
                    <button className="secondary" onClick={() => setConfirmClearMemories(true)} style={{ fontSize: '0.786rem' }}>
                      Limpar memórias
                    </button>
                  )}
                </div>
              )}

              <div className="flex gap-sm">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
                <button className="secondary" onClick={clearForm}>Cancelar</button>
                {selected && <button className="danger" onClick={() => setConfirmDelete(selected.id)}>Excluir</button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog open={!!confirmDelete} title="Excluir empresa"
        message="Tem certeza? Todas as configurações, bots e regras associadas serão removidas."
        confirmLabel="Excluir" onConfirm={() => handleDelete(confirmDelete!)}
        onCancel={() => setConfirmDelete(null)} />

      <ConfirmDialog open={confirmClearMemories} title="Limpar memórias da IA"
        message="Todas as memórias de clientes serão apagadas. O agente IA perderá o histórico de interações anteriores."
        confirmLabel="Limpar" variant="primary" onConfirm={handleClearMemories}
        onCancel={() => setConfirmClearMemories(false)} />
    </div>
  );
}
